/**
 * The finale's three recognitions with where the player stands on each, earned or not (content §5; CP-1 RECOGNITION_RULES). The server
 * names the winners; the measure behind each is read from what the player already holds: the released rounds' reports and the debrief's rounds.
 */
import type { RoundReport, SoloDebrief } from "@bsim/contracts";
import { RISK_STEWARD_RULE } from "@gxs/metric-defs";
import { RECOGNITION_KINDS, type RecognitionKind } from "@bsim/domain";
import { formatKpi, formatUsdAShare } from "../format";
import { kpiLabel } from "../format/labels";
import { lookThroughPp } from "../results/lookThrough";
import { RECOGNITION_LABELS, printedParts } from "../score/labels";
import { beatsBaseline, callLine } from "../results/derive";
import type { KpiId } from "@bsim/domain";

export interface RecognitionStanding {
  readonly kind: RecognitionKind;
  readonly label: string;
  /** What the recognition asks of a bank. */
  readonly needs: string;
  /** How the player did against it; null while the rounds' reports have not loaded. */
  readonly yours: string | null;
  /** The calls or breaches behind `yours`, one line each (round · measure · value against the bar). */
  readonly details: readonly string[];
  readonly earned: boolean;
  /** How to earn it, while it is not earned (null when the recognition has no such line). */
  readonly improve: string | null;
  /** The server's citation, once the recognition is earned, and the bar it was earned over (call skill; forecaster only). */
  readonly citation: string | null;
  readonly par: number | null;
}

const NEEDS: Readonly<Record<RecognitionKind, string>> = {
  forecaster: "Forecasts that beat \"same as last year\" on average, over at least two forecast years. A forecast beats it when it lands closer to where your bank ends the year with no change to your plan than last year's value did.",
  riskSteward: `${RISK_STEWARD_RULE} Your bank must also never have been rated 3 or worse overall by the regulator.`,
  builtToLast: "No give-back when the look-through ran.",
};

/** One line on how to earn a recognition that was missed (shown only while it is not earned): how the calls are judged, and what improves them. */
const IMPROVE: Readonly<Partial<Record<RecognitionKind, string>>> = {
  forecaster: "Each call is judged by how close it lands to where your bank ends the year if you change nothing, against how close 'same as last year' would have landed. To improve, read the rate path and the credit cycle in the Outlook before you call, and move off last year's value only where you can say why.",
};

type ForecastRow = RoundReport["forecastErrors"][number];

const countOf = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

const rowsOf = (reports: readonly RoundReport[]): { readonly roundNo: number; readonly row: ForecastRow }[] =>
  reports.flatMap((r) => r.forecastErrors.filter((f) => f.scored).map((row) => ({ roundNo: r.roundNo, row })));

/** What the player did against a recognition: a one-line summary and the rows behind it (null summary while the rounds' reports have not loaded). */
function yoursFor(kind: RecognitionKind, debrief: SoloDebrief, reports: readonly RoundReport[]): { readonly yours: string | null; readonly details: readonly string[] } {
  if (kind === "builtToLast") {
    const last = debrief.rounds.at(-1);
    if (last === undefined) return { yours: null, details: [] };
    const move = lookThroughPp(last);
    // Dollars lead, points follow (the line beside it on the same finale says "took back $0.30"): "gave back $0.30 a share (0.55 pts)".
    const lastReport = reports.at(-1);
    const usd = lastReport?.score?.components === undefined ? null : printedParts(lastReport.score.components, lastReport.score.valueCreatedUsdPerShare).lookThrough;
    const pts = move.pp.replace(/ points?$/, " pts");
    const both = (verb: string, dollars: number): string => `${verb} ${formatUsdAShare(Math.abs(dollars))} (${pts})`;
    const yours = move.verb === "changed nothing" ? "gave back nothing" : usd === null || Math.abs(usd) < 0.005 ? (move.verb === "subtracted" ? `gave back ${move.pp}` : `gained ${move.pp}`) : both(usd < 0 ? "gave back" : "gained", usd);
    // The detail says the same thing in the look-through's own terms, so the fold never opens onto nothing.
    return { yours, details: [`After year ${last.roundNo} · the look-through ${yours}`] };
  }
  if (reports.length === 0) return { yours: null, details: [] };
  if (kind === "forecaster") {
    const rows = rowsOf(reports);
    return {
      yours: rows.length === 0 ? "no forecasts scored" : `${rows.filter((r) => beatsBaseline(r.row)).length} of ${countOf(rows.length, "forecast")} closer than \"same as last year\"`,
      details: rows.map(({ roundNo, row }) => {
        // Both formats read as the Results page reads them (one wording): the no-change plan's move, the bank's own where it is on the same basis, and a tie is not a beat.
        return `Year ${roundNo} · ${callLine(row, reports.find((r) => r.roundNo === roundNo)?.kpis[row.kpi as KpiId]).text}`;
      }),
    };
  }
  const breaches = reports.flatMap((r) => r.riskAppetiteBreaches.map((b) => `Year ${r.roundNo} · ${kpiLabel(b.kpi as KpiId)}: ${formatKpi(b.kpi as KpiId, b.actual)} at its worst quarter-end, against your limit of ${formatKpi(b.kpi as KpiId, b.limit)}`));
  // The count names what it counts: distinct limits (the names beside it), and when a limit broke in more than one year, the years it did so (one test per limit per year, at its worst quarter-end).
  const names = [...new Set(reports.flatMap((r) => r.riskAppetiteBreaches.map((b) => kpiLabel(b.kpi as KpiId))))];
  const breachedLine = breaches.length === names.length ? `${countOf(names.length, "limit")} breached (${names.join(", ")})` : `${countOf(names.length, "limit")} breached (${names.join(", ")}), ${breaches.length} times counting each year`;
  // The gate sentence is said only when a gate really put the bank last in some round (a gated bank that still stood above the field was not).
  const gatedLast = reports.some((r) => r.scorecard.gateRankedLast && r.scorecard.rank === debrief.rankRace.filter((x) => x.roundNo === r.roundNo).length);
  return { yours: `${breaches.length === 0 ? "no limit breached" : breachedLine}${gatedLast ? ", ranked last because the regulator rated it 3 or worse" : ""}`, details: breaches };
}

/** The rounds with a breach, said in words: "round 1", "rounds 1, 3 and 4"; null when none. */
export function breachRoundsOf(reports: readonly RoundReport[]): string | null {
  const rounds = reports.filter((r) => r.riskAppetiteBreaches.length > 0).map((r) => r.roundNo).sort((a, b) => a - b);
  return rounds.length === 0 ? null : `${rounds.length === 1 ? "round" : "rounds"} ${rounds.length < 2 ? rounds.join("") : `${rounds.slice(0, -1).join(", ")} and ${rounds[rounds.length - 1]}`}`;
}

export function recognitionStandings(debrief: SoloDebrief, teamId: string, reports: readonly RoundReport[]): RecognitionStanding[] {
  return RECOGNITION_KINDS.map((kind) => {
    const won = debrief.recognitions.find((r) => r.kind === kind && r.teamIds.includes(teamId));
    return { kind, label: RECOGNITION_LABELS[kind], needs: won?.rule ?? NEEDS[kind], ...yoursFor(kind, debrief, reports), earned: won !== undefined, improve: won === undefined ? IMPROVE[kind] ?? null : null, citation: won?.citation ?? null, par: won?.par ?? null };
  });
}
